"use client";

import { useTranslations } from "next-intl";
import { Download, Printer } from "lucide-react";

import { Button } from "@/components/ui/button";
import type { PdfAction } from "@/features/problem-set-pdf/pdf-delivery";
import { PdfRunStatus } from "@/features/problem-set-pdf/PdfControls";
import { problemSetPdfPath } from "../api";
import { useContestPdf } from "../hooks";
import { fallbackPdfName } from "../lib/problem-set-pdf";

/**
 * The problem set's one-press Download PDF and Print, for a round's authors
 * and testers outside the console (`EX-PDF02`, Wave 26 decision 7, D3): on
 * the Studio home's "Rounds you author". The server answers them on
 * `staff_read` at any phase; a volunteer never reaches a surface that draws
 * this, and the server refuses one anyway.
 */
export function ProblemSetPdfActions({
  contestId,
  slug,
  title,
}: {
  contestId: string;
  slug: string;
  title: string;
}) {
  const t = useTranslations("contest.problemSetPdf");
  const pdf = useContestPdf(contestId);
  const making = pdf.run.status === "making";

  const start = (action: PdfAction) =>
    void pdf.start({
      path: problemSetPdfPath(contestId),
      fallbackFilename: fallbackPdfName(slug),
      action,
      waiting: t("printWaiting"),
    });

  return (
    <div className="w-full space-y-1.5">
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-11 sm:h-8"
          aria-label={t("card.download", { title })}
          aria-busy={pdf.run.status === "making" && pdf.run.action === "download"}
          disabled={making}
          onClick={() => start("download")}
        >
          <Download aria-hidden className="h-3.5 w-3.5" />
          <span>{t("download")}</span>
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-11 sm:h-8"
          aria-label={t("card.print", { title })}
          aria-busy={pdf.run.status === "making" && pdf.run.action === "print"}
          disabled={making}
          onClick={() => start("print")}
        >
          <Printer aria-hidden className="h-3.5 w-3.5" />
          <span>{t("dialog.print")}</span>
        </Button>
      </div>
      <PdfRunStatus run={pdf.run} />
    </div>
  );
}
